import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormControl, Tab, Grid, Table } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, ExcelUpload } from "@/comn/components";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";
import { colUtils } from "@/tz/go/egaz/osci/colt/comn";
import dayjs from "dayjs";

import {
    useForm,
    useFetch,
    useGrid,
    useResource,
    usePopup,
    useStore,
    useToast,
    useAuth,
    useModal,
    useTab,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_FEE_RGST_SEARCH_FORM,
    SG_GRID_LPCO_FEE_RGST_LIST,
} from "@/tz/go/egaz/osci/lpco/fee/services/OsciLpcoFeeService";

/*
 * List Payment history information
 * !== 납부 내역 정보 목록 화면 ==!
 */
export const OSCI0205007Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    // const pgeTitle = "Paid Notice"; // Page Title !== 페이지 타이틀 ==!
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0006", "auth_osci0008", "auth_osci0009", "auth_osci0021", "auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const [feePrcssStatCds, setFeePrcssStatCds] = useState(["WI", "WB"]); // Fee Processing Status !== 수수료 처리 상태 ==!

    const popup = usePopup(); // Popup Hook !== Popup Hook ==!
    /*
     * Code Resource
     * !== 사용할 Code 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0144" }, // Fee Processing Status Code !== 처리상태코드 ==!
            { area: "comnCd", comnCd: "OSC0121" }, // Application State Code !== 신청서_상태_코드 ==!
            { area: "comnCd", comnCd: "OSC0058" },
        ],
    });

    // Application LIST
    const [appCdNoList, setAppCdNoList] = useState([]);

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Search Fee List Form !== 수수료 검색 Form ==!
        lpcoFeeSrchForm: useForm({
            defaultSchema: SF_FEE_RGST_SEARCH_FORM,
            defaultValues: {
                ...(pgeStore?.form || { feePrcssStatCds: ["WI", "WB"] }),
                frstRgsrDtStr: comnUtils.getDate({ m: -1 }),
                frstRgsrDtEnd: comnUtils.getDate(),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Paid Notice !==  납부 내역 목록 ==!
        lpcoFeePaidList: useGrid({
            defaultSchema: SG_GRID_LPCO_FEE_RGST_LIST,
            page: 0,
            size: 10,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Application List !== Application 목록 ==!
        getOrgLpcoList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoList(auth.get("orgCd"));
            },
            onSuccess: (data) => {
                setAppCdNoList(data || []);
            },
            onError: () => {
                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),
        // Get - LPCO Fee Paid Notice !== LPCO 수수료 납부 내역 목록 ==!
        getLpcoFeeList: useFetch({
            api: (page = grid.lpcoFeePaidList.page) => {
                let data = {
                    ...form.lpcoFeeSrchForm.getValues(),
                    aplcTin: auth.get("tin"),
                    orgCd: auth.get("orgCd"),
                };

                return APIS.getLpcoFeeList(data, page, grid.lpcoFeePaidList.size);
            },
            enabled: comnUtils.isEmpty(form.lpcoFeeSrchForm.errors) && form.lpcoFeeSrchForm.isSubmitted,
            key: [grid.lpcoFeePaidList.page, grid.lpcoFeePaidList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.lpcoFeeSrchForm.getValues(),
                    page: grid.lpcoFeePaidList.page,
                    size: grid.lpcoFeePaidList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        setInit: async () => {
            await fetch.getOrgLpcoList.fetch();
        },
        // Get - LPCO Fee Paid Notice !== LPCO 수수료 납부 내역 목록 ==!
        getLpcoFeeList: (page: number = 0) => {
            form.lpcoFeeSrchForm.handleSubmit(
                () => {
                    grid.lpcoFeePaidList.setPage(page);
                    fetch.getLpcoFeeList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    const render = {
        grid_lpcoFeePaidList: {
            cell: {
                appNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <Link
                            to={`${URLS.osci0205008s}/${rowValues.feeId}`}
                            title={`${rowValues.appNm}`}
                        >{`${rowValues.appNo}`}</Link>
                    );
                },
                bllNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (!!!rowValues.bllNo || rowValues.bllNo === "") {
                        return "[ Not Issued ]";
                    } else {
                        return rowValues.appNo;
                    }
                },
                cmbiBllYn: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (!!rowValues.cmbiBllYn && rowValues.cmbiBllYn === "Y") {
                        return "Combined Bill";
                    } else {
                        return "Normal Bill";
                    }
                },
                rmrk: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (
                        rowValues.feeImpsCtraCd &&
                        rowValues.feeImpsCtraCd === "N" &&
                        Number(rowValues.bllTzsAmt) === 0
                    ) {
                        return "Waiting for interface receipt";
                    } else if (
                        rowValues.cmbiBllYn &&
                        rowValues.cmbiBllYn === "Y" &&
                        rowValues.feeImpsCtraCd &&
                        rowValues.feeImpsCtraCd === "M" &&
                        rowValues.cfrmYn &&
                        rowValues.cfrmYn === "N"
                    ) {
                        return "Need Confirm";
                        // } else if (
                        //     (!!!rowValues.cmbiBllYn || rowValues.cmbiBllYn === "N") &&
                        //     rowValues.feeImpsCtraCd &&
                        //     rowValues.feeImpsCtraCd === "M"
                        // ) {
                        //     return "Waiting for manual issuance";
                        // } else {
                        //     return "";
                    }
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.setInit();
        handler.getLpcoFeeList(pgeStore?.page);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoFeeSrchForm.schema.srchAppNo} />
                            <Group.Control {...form.lpcoFeeSrchForm.schema.srchUcrNo} />
                        </Group.Row>

                        <Group.Row>
                            <Group.Control
                                {...form.lpcoFeeSrchForm.schema.frstRgsrDt}
                                rangeButton={0}
                                controlSize={10}
                            />
                        </Group.Row>

                        <Group.Row>
                            <Group.Control
                                {...form.lpcoFeeSrchForm.schema.feePrcssStatCds}
                                onChange={(value) => {
                                    setFeePrcssStatCds(["value"]);
                                }}
                            ></Group.Control>
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="reset"
                                onClick={() => {
                                    form.lpcoFeeSrchForm.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getLpcoFeeList();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    grid.lpcoFeePaidList.exportExcel({
                                        /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
                                        excelName: dayjs().format("YYYY-MM-DD HH:mm:ss") + "_" + pgeUid,
                                        /* 다운로드 엑셀 시트명 , 기본값 Sheet */
                                        sheetName: "Sheet Name",
                                    });
                                }}
                            />
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.lpcoFeePaidList.grid}
                            data={fetch.getLpcoFeeList.data?.lpcoFeeList}
                            render={render.grid_lpcoFeePaidList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
